import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer, Button, Badge, Spinner, EmptyState, useConfirm, useToast } from "../primitives";
import { Icon } from "../Icon";
import {
  deleteNotification,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../helpers/kronaly_helper";
import { routeForNotification } from "../../helpers/notification_routes";
import useCurrentMembership from "../../helpers/useCurrentMembership";

const TYPE_META = {
  absence_requested: { icon: "Inbox", color: "var(--kr-info-700)", bg: "var(--kr-status-info-bg)" },
  absence_approved:  { icon: "CheckCircle2", color: "var(--kr-success-700)", bg: "var(--kr-status-success-bg)" },
  absence_rejected:  { icon: "XCircle", color: "var(--kr-danger-700)", bg: "var(--kr-status-danger-bg)" },
  absence_cancelled: { icon: "XCircle", color: "var(--kr-fg-muted)", bg: "var(--kr-bg-subtle)" },
  document_requested: { icon: "FileText", color: "var(--kr-info-700)", bg: "var(--kr-status-info-bg)" },
  document_request_fulfilled: { icon: "CheckCircle2", color: "var(--kr-success-700)", bg: "var(--kr-status-success-bg)" },
  overtime_requested: { icon: "Clock", color: "var(--kr-info-700)", bg: "var(--kr-status-info-bg)" },
  overtime_approved:  { icon: "CheckCircle2", color: "var(--kr-success-700)", bg: "var(--kr-status-success-bg)" },
  overtime_rejected:  { icon: "XCircle", color: "var(--kr-danger-700)", bg: "var(--kr-status-danger-bg)" },
  overtime_cancelled: { icon: "XCircle", color: "var(--kr-fg-muted)", bg: "var(--kr-bg-subtle)" },
  workday_correction_requested: { icon: "Pencil", color: "var(--kr-warning-700)", bg: "var(--kr-status-warning-bg)" },
  workday_correction_approved:  { icon: "CheckCircle2", color: "var(--kr-success-700)", bg: "var(--kr-status-success-bg)" },
  workday_correction_rejected:  { icon: "XCircle", color: "var(--kr-danger-700)", bg: "var(--kr-status-danger-bg)" },
  workday_correction_cancelled: { icon: "XCircle", color: "var(--kr-fg-muted)", bg: "var(--kr-bg-subtle)" },
  special_schedule_requested: { icon: "CalendarClock", color: "var(--kr-warning-700)", bg: "var(--kr-status-warning-bg)" },
  special_schedule_approved:  { icon: "CalendarCheck", color: "var(--kr-success-700)", bg: "var(--kr-status-success-bg)" },
  special_schedule_rejected:  { icon: "CalendarX", color: "var(--kr-danger-700)", bg: "var(--kr-status-danger-bg)" },
  report_ready:  { icon: "FileBarChart", color: "var(--kr-success-700)", bg: "var(--kr-status-success-bg)" },
  report_failed: { icon: "XCircle", color: "var(--kr-danger-700)", bg: "var(--kr-status-danger-bg)" },
  shift_approaching_limit: { icon: "AlertTriangle", color: "var(--kr-warning-700)", bg: "var(--kr-status-warning-bg)" },
  shift_limit_reached:     { icon: "AlarmClock", color: "var(--kr-danger-700)", bg: "var(--kr-status-danger-bg)" },
  workday_incident_reported:  { icon: "Flag", color: "var(--kr-warning-700)", bg: "var(--kr-status-warning-bg)" },
  workday_incident_validated: { icon: "CheckCircle2", color: "var(--kr-success-700)", bg: "var(--kr-status-success-bg)" },
  overtime_no_authorization:  { icon: "ShieldAlert", color: "var(--kr-danger-700)", bg: "var(--kr-status-danger-bg)" },
  default: { icon: "Bell", color: "var(--kr-fg-muted)", bg: "var(--kr-bg-subtle)" },
};

const PRIORITY_LABEL = {
  high: "Urgente",
};

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - then.getTime()) / 1000));
  if (diffSec < 60) return "Justo ahora";
  if (diffSec < 3600) return `Hace ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `Hace ${Math.floor(diffSec / 3600)} h`;
  if (diffSec < 86400 * 2) return "Ayer";
  if (diffSec < 86400 * 7) return `Hace ${Math.floor(diffSec / 86400)} días`;
  return then.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

export function NotificacionesPanel({ open, onClose, onUpdated }) {
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { employeeProfileId } = useCurrentMembership();
  const [tab, setTab] = useState("todas");
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getNotifications();
      const payload = unwrap(r) || {};
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setUnreadCount(Number(payload.unread_count) || 0);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar las notificaciones.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const filtered = useMemo(() => {
    if (tab === "noleidas") return items.filter((n) => !n.read_at);
    if (tab === "prioritarias") return items.filter((n) => n.priority === "high");
    return items;
  }, [items, tab]);

  const onMarkRead = async (n) => {
    if (n.read_at) return;
    try {
      await markNotificationRead(n.id);
      await load();
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error("No se pudo marcar como leída", typeof e === "string" ? e : e?.message);
    }
  };

  const onDelete = async (n) => {
    const ok = await confirm({
      title: "Eliminar notificación",
      description: "Se eliminará de tu bandeja. No se puede deshacer.",
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteNotification(n.id);
      await load();
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error("No se pudo eliminar", typeof e === "string" ? e : e?.message);
    }
  };

  const onMarkAll = async () => {
    if (unreadCount === 0) return;
    try {
      await markAllNotificationsRead();
      await load();
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error("No se pudieron marcar todas como leídas", typeof e === "string" ? e : e?.message);
    }
  };

  /**
   * Click en el cuerpo de una notificación con ruta destino: marca como
   * leída en background (feedback inmediato) y navega. Si no hay ruta,
   * el cuerpo no es clicable — el usuario actúa vía botones explícitos.
   */
  const onActivate = (n) => {
    const route = routeForNotification(n, { employeeProfileId });
    if (!route) return;
    if (!n.read_at) {
      markNotificationRead(n.id).catch(() => {});
      if (onUpdated) onUpdated();
    }
    onClose?.();
    navigate(route);
  };

  const title = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Icon name="Bell" size={18} style={{ color: "var(--kr-fg-strong)" }} />
      <span style={{ fontSize: 16, fontWeight: 700, color: "var(--kr-fg-strong)" }}>Notificaciones</span>
      {unreadCount > 0 && <Badge tone="brand">{unreadCount} sin leer</Badge>}
    </div>
  );

  const footer = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
      <span style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
        Mostrando {filtered.length} de {items.length}
      </span>
      <Button
        variant="ghost"
        size="sm"
        icon="Settings2"
        onClick={() => {
          onClose?.();
          navigate("/ajustes/notificaciones");
        }}
      >
        Preferencias
      </Button>
    </div>
  );

  const tabs = [
    { id: "todas", label: "Todas", count: items.length },
    { id: "noleidas", label: "Sin leer", count: items.filter((n) => !n.read_at).length },
    { id: "prioritarias", label: "Prioritarias", count: items.filter((n) => n.priority === "high").length },
  ];

  return (
    <Drawer open={open} onClose={onClose} size="sm" title={title} footer={footer}>
      <div style={{ margin: "-20px -20px 0", display: "flex", flexDirection: "column", height: "calc(100% + 20px)" }}>
        <div style={{ display: "flex", padding: "8px 12px", gap: 4, borderBottom: "1px solid var(--kr-border-subtle)", alignItems: "center" }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                border: "none",
                background: tab === t.id ? "var(--kr-action-soft-bg)" : "transparent",
                color: tab === t.id ? "var(--kr-action-soft-fg)" : "var(--kr-fg-muted)",
                padding: "6px 10px",
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 4,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {t.label}
              <span style={{ fontSize: 10, opacity: 0.7, fontFamily: "var(--kr-font-mono)" }}>{t.count}</span>
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <Button variant="ghost" size="sm" onClick={onMarkAll} disabled={unreadCount === 0}>
            Marcar todo leído
          </Button>
        </div>

        <div style={{ flex: 1, overflow: "auto" }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: "center" }}><Spinner size={16} /></div>
          ) : error ? (
            <div style={{ padding: 16, color: "var(--kr-danger-700)", fontSize: 13 }}>{error}</div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon="Bell"
              title={tab === "todas" ? "Todo al día" : "No hay nada por aquí"}
              description="Las notificaciones aparecerán aquí cuando ocurran cambios relevantes."
            />
          ) : (
            filtered.map((n) => {
              const meta = TYPE_META[n.type] || TYPE_META.default;
              const isRead = Boolean(n.read_at);
              const hasRoute = Boolean(routeForNotification(n, { employeeProfileId }));
              return (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  meta={meta}
                  isRead={isRead}
                  hasRoute={hasRoute}
                  onActivate={() => onActivate(n)}
                  onMarkRead={() => onMarkRead(n)}
                  onDelete={() => onDelete(n)}
                />
              );
            })
          )}
        </div>
      </div>
    </Drawer>
  );
}

/**
 * Renderizado de una notificación. El área superior (icono + texto) es
 * clicable cuando hay ruta destino — al activarla, navega al recurso y
 * marca leída. Los botones inferiores ("Marcar leído" / "Eliminar")
 * tienen stopPropagation para no disparar el click del contenedor.
 */
function NotificationRow({ notification: n, meta, isRead, hasRoute, onActivate, onMarkRead, onDelete }) {
  const [hover, setHover] = useState(false);
  const interactive = hasRoute;
  const onKey = (e) => {
    if (interactive && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onActivate();
    }
  };

  return (
    <div
      style={{
        padding: "14px 20px",
        borderBottom: "1px solid var(--kr-border-subtle)",
        background: hover && interactive
          ? "var(--kr-bg-subtle)"
          : isRead
            ? "#fff"
            : "var(--kr-neutral-50)",
        position: "relative",
        cursor: interactive ? "pointer" : "default",
        transition: "background var(--kr-dur-fast) var(--kr-ease-out)",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={interactive ? onActivate : undefined}
      onKeyDown={interactive ? onKey : undefined}
      tabIndex={interactive ? 0 : undefined}
      role={interactive ? "button" : undefined}
    >
      {!isRead && (
        <div
          style={{
            position: "absolute",
            left: 8,
            top: 22,
            width: 6,
            height: 6,
            borderRadius: 999,
            background: "var(--kr-action)",
          }}
        />
      )}
      <div style={{ display: "flex", gap: 12 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: meta.bg,
            color: meta.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon name={meta.icon} size={14} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
            {PRIORITY_LABEL[n.priority] && <Badge tone="danger">{PRIORITY_LABEL[n.priority]}</Badge>}
            <span style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{relativeTime(n.created_at)}</span>
            {interactive && hover && (
              <span style={{ marginLeft: "auto", fontSize: 10, color: "var(--kr-fg-muted)", display: "inline-flex", alignItems: "center", gap: 2 }}>
                Ver <Icon name="ArrowRight" size={10} />
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: isRead ? 500 : 600,
              color: "var(--kr-fg-strong)",
              lineHeight: 1.35,
            }}
          >
            {n.title}
          </div>
          {n.body && (
            <div
              style={{
                fontSize: 12,
                color: "var(--kr-fg-muted)",
                marginTop: 2,
                whiteSpace: "pre-wrap",
              }}
            >
              {n.body}
            </div>
          )}
          <div style={{ marginTop: 8, display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
            {!isRead && (
              <Button size="sm" variant="ghost" onClick={onMarkRead}>
                Marcar leído
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onDelete}>
              Eliminar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificacionesPanel;
